/**
 * AccountUsersDialog — manage the SSO users of one account (create / delete),
 * backed by the dedicated /account/{id}/sso-users endpoints (admin 1.0.26).
 * Membership lives in the account's Keycloak group, so these are exactly the
 * people who can sign into the customer portal and the contact center.
 */

import React, { useEffect, useState } from "react";
import { useNotify, useTranslate } from "react-admin";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import {
  Close as CloseIcon,
  GroupOutlined as UsersIcon,
  DeleteOutlined as DeleteIcon,
  PersonAddAltOutlined as AddUserIcon,
  LoginOutlined as ImpersonateIcon,
  StorefrontOutlined as PortalIcon,
  SupportAgentOutlined as AgentIcon,
  VpnKeyOutlined as KeyIcon,
} from "@mui/icons-material";
import { useCanManageAccounts, useIsSuperUser } from "@/utils/permissions";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  AccountSsoUser,
  createAccountSsoUser,
  deleteAccountSsoUser,
  listAccountSsoUsers,
  getAccountScopeOptions,
  updateAccountSsoUser,
  type AccountScopeOptions,
} from "../../services/accountService";
import {
  openImpersonation,
  type ImpersonationTarget,
} from "../../services/impersonation";

const i18n = "resources.accounts";

export interface AccountUsersDialogProps {
  accountId: string;
  accountName: string;
  open: boolean;
  onClose: () => void;
}

export const AccountUsersDialog: React.FC<AccountUsersDialogProps> = ({
  accountId,
  accountName,
  open,
  onClose,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const canEdit = useCanManageAccounts();
  const canImpersonate = useIsSuperUser();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<AccountSsoUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AccountSsoUser | null>(null);
  const [impersonate, setImpersonate] = useState<AccountSsoUser | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  /** Lo que ESTA cuenta puede repartir; sin esto se ofrecerian permisos que el servidor recorta. */
  const [options, setOptions] = useState<AccountScopeOptions | null>(null);
  /** Usuario cuyos permisos se estan editando en la lista, y el borrador de sus casillas. */
  const [editing, setEditing] = useState<AccountSsoUser | null>(null);
  const [editScopes, setEditScopes] = useState<string[]>([]);

  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`${i18n}.usersDialog.${key}`, options);

  const load = React.useCallback(() => {
    setLoading(true);
    void getAccountScopeOptions(accountId)
      .then((o) => {
        setOptions(o);
        // El alta arranca con lo minimo util —entrar al portal— y no con todo marcado:
        // repartir permisos de mas es justo lo que este cambio viene a evitar.
        setScopes(o.allowedScopes.includes("portal") ? ["portal"] : []);
      })
      .catch(() => setOptions(null));
    listAccountSsoUsers(accountId)
      .then(setUsers)
      .catch((err: unknown) => {
        notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
          type: "error",
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  useEffect(() => {
    if (!open) return;
    setAddOpen(false);
    setEmail("");
    setName("");
    setSurname("");
    setPassword("");
    setPhone("");
    setTelegramChatId("");
    setEditing(null);
    load();
  }, [open, load]);

  const create = async (): Promise<void> => {
    setBusy(true);
    try {
      await createAccountSsoUser(accountId, {
        email: email.trim(),
        name: name.trim() || undefined,
        surname: surname.trim() || undefined,
        password: password || undefined,
        phone: phone.trim() || undefined,
        telegramChatId: telegramChatId.trim() || undefined,
        scopes,
      });
      notify(t("created"), { type: "success" });
      setAddOpen(false);
      setEmail("");
      setName("");
      setSurname("");
      setPassword("");
      setPhone("");
      setTelegramChatId("");
      load();
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await deleteAccountSsoUser(accountId, confirmDelete.keycloakId);
      notify(t("deleted"), { type: "success" });
      setConfirmDelete(null);
      load();
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const startImpersonation = async (target: ImpersonationTarget): Promise<void> => {
    if (!impersonate) return;
    setBusy(true);
    try {
      const userName =
        [impersonate.firstName, impersonate.lastName].filter(Boolean).join(" ") ||
        impersonate.email ||
        impersonate.keycloakId;
      await openImpersonation(target, {
        userSub: impersonate.keycloakId,
        userName,
        accountName,
      });
      setImpersonate(null);
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <UsersIcon sx={{ fontSize: 18, color: rg.text3 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.02em" }}>
            {t("title", { name: accountName })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Typography sx={{ px: "20px", py: "10px", fontSize: "12px", color: rg.text3 }}>
          {t("hint")}
        </Typography>

        {loading && (
          <Box sx={{ display: "grid", placeItems: "center", py: 5 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!loading && users.length === 0 && (
          <Typography
            sx={{ py: 4, textAlign: "center", fontSize: "12.5px", color: rg.text3 }}
          >
            {t("empty")}
          </Typography>
        )}

        {!loading &&
          users.map((u, i) => (
            <Box
              key={u.keycloakId}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: "20px",
                py: "10px",
                borderTop: `1px solid ${rg.border}`,
                borderBottom:
                  i === users.length - 1 ? `1px solid ${rg.border}` : "none",
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontFamily: rg.fontMono,
                    color: rg.text3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {u.email}
                </Typography>
              </Box>
              {/* Los permisos, a la vista en la propia fila: sin esto hay que abrir a cada
                  usuario para saber quien puede que. En ambar los que todavia no se han
                  configurado y salen del legado. */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 320 }}>
                {(u.scopes ?? []).map((sc) => (
                  <Chip
                    key={sc}
                    size="small"
                    label={translate(`resources.user-management.scopes.${sc}`)}
                    sx={{
                      fontSize: 10.5,
                      height: 20,
                      backgroundColor: u.scopesConfigured ? rg.info.bg : rg.warn.bg,
                      color: u.scopesConfigured ? rg.info.fg : rg.warn.fg,
                    }}
                  />
                ))}
                {(u.scopes ?? []).length === 0 && (
                  <Typography sx={{ fontSize: 11, color: rg.text3 }}>
                    {t("noScopes")}
                  </Typography>
                )}
              </Box>
              {!u.enabled && (
                <Chip
                  size="small"
                  label={t("disabled")}
                  sx={{ fontSize: 10.5, height: 20 }}
                />
              )}
              {canEdit && (
                <Tooltip title={t("editScopes")}>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      setEditing(u);
                      setEditScopes(u.scopes ?? []);
                    }}
                    sx={{ color: rg.text3 }}
                  >
                    <KeyIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {canImpersonate && u.enabled && (
                <Tooltip title={t("impersonate")}>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() => setImpersonate(u)}
                    sx={{ color: rg.text3 }}
                  >
                    <ImpersonateIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {canEdit && (
                <Tooltip title={t("removeFromAccount")}>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() => setConfirmDelete(u)}
                    sx={{ color: rg.danger.line }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}

        {canEdit && addOpen && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              px: "20px",
              py: "14px",
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <TextField
                label={t("email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                sx={{ flex: 2, minWidth: 220 }}
              />
              <TextField
                label={t("name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
              />
              <TextField
                label={t("surname")}
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 140 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <TextField
                label={t("phone")}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 180 }}
              />
              <TextField
                label={t("telegramChatId")}
                value={telegramChatId}
                onChange={(e) => setTelegramChatId(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 180 }}
              />
            </Box>
            <TextField
              label={t("password")}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="small"
              fullWidth
            />
            <FormGroup row>
              {(options?.allowedScopes ?? []).map((sc) => (
                <FormControlLabel
                  key={sc}
                  control={
                    <Checkbox
                      size="small"
                      checked={scopes.includes(sc)}
                      onChange={(_e, checked) =>
                        setScopes((prev) =>
                          checked ? [...prev, sc] : prev.filter((x) => x !== sc),
                        )
                      }
                    />
                  }
                  label={translate(`resources.user-management.scopes.${sc}`)}
                />
              ))}
            </FormGroup>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
              <Button size="small" disabled={busy} onClick={() => setAddOpen(false)}>
                {translate("ra.action.cancel")}
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={busy || !email.trim().includes("@")}
                onClick={() => void create()}
              >
                {t("create")}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      {canEdit && !addOpen && (
        <DialogActions>
          <Button
            startIcon={<AddUserIcon sx={{ fontSize: 17 }} />}
            variant="contained"
            size="small"
            onClick={() => setAddOpen(true)}
          >
            {t("add")}
          </Button>
        </DialogActions>
      )}

      {/* Impersonate: pick destination */}
      <Dialog
        open={impersonate !== null}
        onClose={() => !busy && setImpersonate(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { width: 440, maxWidth: "90vw" } }}
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.02em" }}>
          {t("impersonateTitle", {
            name:
              [impersonate?.firstName, impersonate?.lastName]
                .filter(Boolean)
                .join(" ") ||
              impersonate?.email ||
              "",
          })}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "12.5px", color: rg.text3, lineHeight: 1.55, mb: 2 }}>
            {t("impersonateHint")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              disabled={busy}
              startIcon={<PortalIcon sx={{ fontSize: 18 }} />}
              onClick={() => void startImpersonation("portal")}
              sx={{ py: 1.25, flexDirection: "column", gap: 0.5, textTransform: "none" }}
            >
              {t("portal")}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              disabled={busy}
              startIcon={<AgentIcon sx={{ fontSize: 18 }} />}
              onClick={() => void startImpersonation("agent")}
              sx={{ py: 1.25, flexDirection: "column", gap: 0.5, textTransform: "none" }}
            >
              {t("contactCenter")}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setImpersonate(null)}>
            {translate("ra.action.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edicion de permisos de un usuario ya existente. Dialogo aparte y no edicion en la
          fila: las casillas no caben, y conviene que guardar sea un gesto explicito. */}
      {editing && (
        <Dialog
          open
          onClose={() => !busy && setEditing(null)}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{ sx: { width: 420, maxWidth: "90vw" } }}
        >
          <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>
            {t("editScopes")}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 12.5, color: rg.text3, mb: 1.5 }}>
              {editing.email}
            </Typography>
            <FormGroup>
              {(options?.allowedScopes ?? []).map((sc) => (
                <FormControlLabel
                  key={sc}
                  control={
                    <Checkbox
                      size="small"
                      checked={editScopes.includes(sc)}
                      onChange={(_e, checked) =>
                        setEditScopes((prev) =>
                          checked ? [...prev, sc] : prev.filter((x) => x !== sc),
                        )
                      }
                    />
                  }
                  label={translate(`resources.user-management.scopes.${sc}`)}
                />
              ))}
            </FormGroup>
          </DialogContent>
          <DialogActions>
            <Button size="small" disabled={busy} onClick={() => setEditing(null)}>
              {translate("ra.action.cancel")}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                updateAccountSsoUser(accountId, editing.keycloakId, { scopes: editScopes })
                  .then(() => {
                    notify(t("scopesSaved"), { type: "success" });
                    setEditing(null);
                    load();
                  })
                  .catch((err: unknown) => {
                    notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
                      type: "error",
                    });
                  })
                  .finally(() => setBusy(false));
              }}
            >
              {translate("ra.action.save")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete confirm */}
      <Dialog
        open={confirmDelete !== null}
        onClose={() => !busy && setConfirmDelete(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { width: 420, maxWidth: "90vw" } }}
      >
        <DialogContent sx={{ textAlign: "center", pt: "24px !important" }}>
          <Typography sx={{ fontSize: "14.5px", fontWeight: 700, mb: 1 }}>
            {t("deleteTitle", { email: confirmDelete?.email ?? "" })}
          </Typography>
          <Typography sx={{ fontSize: "12.5px", color: rg.text3, lineHeight: 1.55 }}>
            {t("deleteContent")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button variant="outlined" disabled={busy} onClick={() => setConfirmDelete(null)}>
            {translate("ra.action.cancel")}
          </Button>
          <Button variant="contained" color="error" disabled={busy} onClick={() => void remove()}>
            {translate("ra.action.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AccountUsersDialog;
